import { useState, useEffect, useCallback } from 'react';
import { navLinks, contactPhone, projectName } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#hero');
            }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden bg-background-50">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20real%20estate%20brand%20logo%20badge%20white%20circle%20with%20green%20lowercase%20iaan%20text%20and%20small%20Korean%20text%20above%20simple%20modern%20apartment%20branding%20style%20flat%20design%20high%20detail%20centered%20composition&width=120&height=120&seq=logo-iaan-navbar-01&orientation=squarish"
                alt="신상도역 이안 로고"
                className="w-full h-full object-cover object-center"
                width={40}
                height={40}
              />
            </div>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-xl md:text-2xl font-semibold tracking-tight transition-colors ${
                  scrolled ? 'text-foreground-900' : 'text-background-50'
                }`}
              >
                {projectName}
              </span>
              <span
                className={`text-xs md:text-sm font-medium tracking-[0.2em] uppercase transition-colors mt-0.5 ${
                  scrolled ? 'text-foreground-400' : 'text-background-50/60'
                }`}
              >
                SINSANGDO IAN
              </span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className={`text-base font-medium transition-colors hover:opacity-80 ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-900'
                    : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${contactPhone}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-base font-semibold whitespace-nowrap transition-all hover:scale-105 ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50/20 text-background-50 hover:bg-background-50/30 backdrop-blur-sm'
              }`}
            >
              <i className="ri-phone-line" />
              전화상담
            </a>
          </div>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              scrolled ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-lg border-t border-background-200/50">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="block px-4 py-3 text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg text-base font-medium transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-2">
              <a
                href={`tel:${contactPhone}`}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-base font-semibold whitespace-nowrap"
              >
                <i className="ri-phone-line" />
                전화상담 {contactPhone}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}